import { createSelector } from '@reduxjs/toolkit';
import { List as ImList, Record as ImRecord, RecordOf } from 'immutable';

import * as dateSelectors from 'app-components/flux/date/dateSelectors';
import { taxDueDates } from 'app-components/flux/scheduled-transactions/scheduledTransactionsUtils';
import type { transactionsTypes } from 'app-components/flux/transactions';
import { transactionsSelectors } from 'app-components/flux/transactions';
import type RootState from 'app-components/utils/redux-store/rootState';

import { TAXES_REDUCER_KEY } from './taxesTypes';

type State = Pick<RootState, typeof TAXES_REDUCER_KEY>;

export const getStore = (state: State) => state[TAXES_REDUCER_KEY];

export const getResourcesById = (state: State) => getStore(state).resourcesById;
export const getLoadPending = (state: State) => getStore(state).loadPending;
export const getIsLoading = (state: State) => getStore(state).isLoading;
export const getError = (state: State) => getStore(state).error;
export const getLastSyncDate = (state: State) => getStore(state).lastSyncDate;

export const getTaxPlans = createSelector(getResourcesById, (taxPlans) => {
  const taxPlansList = taxPlans.valueSeq().toList();
  return taxPlansList.sortBy((taxPlan) => taxPlan.input.year).reverse();
});

export const getTaxPlanByYear = createSelector(
  getResourcesById,
  (_state: unknown, year: number) => year,
  (taxPlans, year) => taxPlans?.find((taxPlan) => taxPlan.input.year === year) || null,
);

export const getCurrentTaxYear = createSelector(dateSelectors.getCurrentDate, (currentDate) => {
  const previous = taxDueDates(currentDate.year - 1);
  return (currentDate?.toISODate() || '') <= previous.q4Due ? currentDate.year - 1 : currentDate.year;
});

type TaxQuartersProps = {
  q1: ImList<transactionsTypes.TransactionRecord>;
  q2: ImList<transactionsTypes.TransactionRecord>;
  q3: ImList<transactionsTypes.TransactionRecord>;
  q4: ImList<transactionsTypes.TransactionRecord>;
};
export type TaxQuarters = RecordOf<TaxQuartersProps>;
const mkTaxQuarters = ImRecord<TaxQuartersProps>({
  q1: ImList(),
  q2: ImList(),
  q3: ImList(),
  q4: ImList(),
});
export const getTaxQuartersTransactions = createSelector(
  (state, stModelId) => transactionsSelectors.getSTModelTransactions(state, stModelId),
  (state, stModelId, year) => year,
  (transactions, year) => {
    const dueDates = taxDueDates(year);
    const taxQuarters = mkTaxQuarters().withMutations((taxQuartersMutable) => {
      transactions.forEach((transaction: transactionsTypes.TransactionRecord) => {
        if (transaction.postedOn <= dueDates.q1Due) {
          taxQuartersMutable.merge({ q1: taxQuartersMutable.q1.push(transaction) });
        } else if (transaction.postedOn > dueDates.q1Due && transaction.postedOn <= dueDates.q2Due) {
          taxQuartersMutable.merge({ q2: taxQuartersMutable.q2.push(transaction) });
        } else if (transaction.postedOn > dueDates.q2Due && transaction.postedOn <= dueDates.q3Due) {
          taxQuartersMutable.merge({ q3: taxQuartersMutable.q3.push(transaction) });
        } else if (transaction.postedOn > dueDates.q3Due) {
          taxQuartersMutable.merge({ q4: taxQuartersMutable.q4.push(transaction) });
        }
      });
      taxQuartersMutable.merge({ q1: taxQuartersMutable.q1.sortBy((transaction) => transaction.postedOn) });
      taxQuartersMutable.merge({ q2: taxQuartersMutable.q2.sortBy((transaction) => transaction.postedOn) });
      taxQuartersMutable.merge({ q3: taxQuartersMutable.q3.sortBy((transaction) => transaction.postedOn) });
      taxQuartersMutable.merge({ q4: taxQuartersMutable.q4.sortBy((transaction) => transaction.postedOn) });
    });
    return taxQuarters;
  },
);
